import { createContext, useContext, useMemo, useCallback, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@apollo/client";
import { MY_WAVE_CONFIG, LOG_PARTICIPANT_ACTIVITY } from "../queries/waves";
import { AUTH_TOKEN } from "../constants/authToken";

/* ============================================================================
 * Wave config bootstrap — the single source of truth for which dark pattern to
 * apply and which arm the participant is in.
 *
 * `myWaveConfig` returns null when the user is not enrolled in a running wave,
 * or is enrolled but the wave has not started. In those cases every helper here
 * resolves to "no dark pattern" so the normal platform renders unchanged.
 *
 * The backend returns `flags` as a JSON string — this provider parses it once
 * and exposes the parsed object.
 * ==========================================================================*/

const WaveConfigContext = createContext({
    config: null,
    loading: false,
    refetch: () => {},
    logActivity: () => {},
});

// Parse the raw myWaveConfig payload into a normalised shape with parsed flags.
function normalise(raw) {
    if (!raw) return null;
    let flags = {};
    try {
        flags = raw.flags ? JSON.parse(raw.flags) : {};
    } catch (_) {
        flags = {};
    }
    return {
        waveId: raw.wave_id,
        darkPattern: raw.dark_pattern,
        phase: raw.phase,
        currentArm: raw.current_arm,
        flags,
    };
}

export function WaveConfigProvider({ children }) {
    // Only query when authenticated — myWaveConfig requires a JWT and returns
    // null otherwise. Skipping avoids a pointless request on the public pages.
    const hasToken = !!localStorage.getItem(AUTH_TOKEN);

    const { data, loading, refetch } = useQuery(MY_WAVE_CONFIG, {
        skip: !hasToken,
        // Wave phase/arm can change server-side (a Phase 3 session fires); re-poll
        // so the participant UI picks up a new arm without a manual reload.
        pollInterval: 60000,
        fetchPolicy: "cache-and-network",
    });

    const config = useMemo(() => normalise(data?.myWaveConfig), [data]);

    // ---- Activity logging (endpointsforfrontend.md §5p) -------------------
    // Fire-and-forget. Only meaningful when enrolled in a running wave; the
    // resolver no-ops otherwise, but we skip the request entirely to be cheap.
    const [logActivityMutation] = useMutation(LOG_PARTICIPANT_ACTIVITY);
    const enrolledRef = useRef(false);
    enrolledRef.current = !!config;

    const logActivity = useCallback((eventType) => {
        if (!enrolledRef.current) return;
        logActivityMutation({ variables: { eventType } }).catch(() => {});
    }, [logActivityMutation]);

    // "foreground_with_interaction": app returned from background AND the
    // participant made their first interaction. Bare foreground does not count.
    useEffect(() => {
        let returnedFromBackground = false;
        const onVisibility = () => {
            if (document.visibilityState === "hidden") returnedFromBackground = true;
        };
        const onInteraction = () => {
            if (!returnedFromBackground) return;
            returnedFromBackground = false;
            logActivity("foreground_with_interaction");
        };
        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pointerdown", onInteraction, true);
        window.addEventListener("keydown", onInteraction, true);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pointerdown", onInteraction, true);
            window.removeEventListener("keydown", onInteraction, true);
        };
    }, [logActivity]);

    const value = useMemo(() => ({
        config,
        loading,
        refetch,
        logActivity,
    }), [config, loading, refetch, logActivity]);

    return (
        <WaveConfigContext.Provider value={value}>
            {children}
        </WaveConfigContext.Provider>
    );
}

export function useWaveConfig() {
    return useContext(WaveConfigContext);
}

// Convenience accessor for the fire-and-forget activity logger.
export function useActivityLogger() {
    return useContext(WaveConfigContext).logActivity;
}

/* --------------------------- Decision helpers -------------------------- */

// Whether a given flag is active. Flags only take effect from Phase 2 onwards —
// Phase 1 is always the clean baseline regardless of flag values.
export function isFlagActive(config, flagKey) {
    if (!config || config.phase < 2) return false;
    return config.flags[flagKey] === "true";
}

// Which close-position flow to show for an obstruction wave.
// Returns "normal" | "four_step" | "single_step".
// Arm decision tree per implementation.md CHANGE 4 / endpointsforfrontend.md §5a:
//   Arm 0 (control)  → four_step (dark pattern continues exactly as Phase 2)
//   Arm A (disclose) → four_step (plus the session-start disclosure modal)
//   Arm B (friction) → single_step (obstruction reduced — single confirm, no timer)
//   Arm C (symmetric)→ four_step (exit unchanged; entry also becomes four-step)
export function getExitFlowType(config) {
    if (!config || config.darkPattern !== "obstruction") return "normal";
    const { phase, currentArm } = config;

    if (phase === 1) return "normal";      // clean baseline
    if (phase === 2) return "four_step";   // all participants, no arm yet

    if (phase === 3) {
        if (currentArm === "B") return "single_step"; // only Arm B reduces friction
        return "four_step";                           // 0, A, C, and null (pre-session)
    }
    return "normal";
}

// Whether the Arm C symmetric 4-step ENTRY flow applies to a Buy for this
// participant. Only Phase 3 Arm C runs entry obstruction; every other
// arm/phase buys normally. (endpointsforfrontend.md §5q, CHANGE 5.)
export function getEntryFlowType(config) {
    if (!config || config.darkPattern !== "obstruction") return "normal";
    if (config.phase === 3 && config.currentArm === "C") return "four_step";
    return "normal";
}

// True only for Phase 3 Arm A participants who have not yet seen the disclosure
// this session. Marks it shown (sessionStorage) so it appears at most once.
export function consumeDisclosureShown(config) {
    if (!config || config.phase !== 3 || config.currentArm !== "A") return false;
    const key = `disclosure_shown_${config.waveId}`;
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, "1");
    return true;
}
